import type { EfacturaStatus } from '@financiar/shared';
import { Tag, Tooltip } from 'antd';

interface TagConfig {
  color?: string;
  full: string;
  short: string;
}

// Colors follow the app's existing Tag conventions (see FacturiListPage's
// StatusTag): blue = in progress, green = success, red = rejected, volcano =
// error/attention, default = neutral/pending.
const CONFIG: Record<Exclude<EfacturaStatus, 'NOT_APPLICABLE'>, TagConfig> = {
  PENDING: { color: undefined, full: 'e-Factura: în așteptare', short: 'În așteptare' },
  UPLOADED: { color: 'blue', full: 'e-Factura: trimisă', short: 'Trimisă' },
  VALIDATED: { color: 'green', full: 'e-Factura: validată', short: 'Validată' },
  REJECTED: { color: 'red', full: 'e-Factura: respinsă', short: 'Respinsă' },
  ERROR: { color: 'volcano', full: 'e-Factura: eroare', short: 'Eroare' },
};

/**
 * Status tag for an invoice's e-Factura lifecycle, styled like the app's other
 * status tags. Renders nothing for NOT_APPLICABLE (never sent). `compact` drops
 * the "e-Factura:" prefix for the list column (whose header already says it).
 * A REJECTED/ERROR tag carries the ANAF message as a tooltip.
 */
export function EfacturaTag({
  status,
  message,
  compact = false,
}: {
  status: EfacturaStatus;
  message?: string | null;
  compact?: boolean;
}) {
  if (status === 'NOT_APPLICABLE') return null;
  const cfg = CONFIG[status];
  const tag = <Tag color={cfg.color}>{compact ? cfg.short : cfg.full}</Tag>;
  if (message && (status === 'REJECTED' || status === 'ERROR')) {
    return <Tooltip title={message}>{tag}</Tooltip>;
  }
  return tag;
}
